/**
 * Compact badge showing the current model with provider icon.
 * Uses a split-segment design: [icon model] | [reasoning effort]
 */
import type { FC } from "react";

import { Text } from "../../common/Text";
import {
  ProviderIcon,
  extractModelName,
  extractProvider,
} from "./ProviderIcon";

interface ModelBadgeProps {
  modelRef: string;
  /** Optional label to show before the model name */
  label?: string;
  /** Optional reasoning effort setting (e.g., "low", "medium", "high") */
  reasoningEffort?: string | null;
}

function effortColor(effort: string): string {
  switch (effort.toLowerCase()) {
    case "high": {
      return "text-violet-600 dark:text-violet-400";
    }
    case "medium": {
      return "text-amber-600 dark:text-amber-400";
    }
    case "low": {
      return "text-slate-500 dark:text-slate-400";
    }
    default: {
      return "text-slate-500 dark:text-slate-400";
    }
  }
}

const EffortIndicator: FC<{ effort: string }> = ({ effort }) => (
  <span
    className={`inline-block h-1.5 w-1.5 ${effortColor(effort)}`}
    aria-hidden="true"
  />
);

/** Compact badge displaying model with provider icon */
export const ModelBadge: FC<ModelBadgeProps> = ({
  modelRef,
  label,
  reasoningEffort,
}) => {
  const provider = extractProvider(modelRef);
  const modelName = extractModelName(modelRef);

  return (
    <div className="inline-flex items-center">
      {label && (
        <Text variant="label" className="mr-2">
          {label}
        </Text>
      )}
      <div className="inline-flex items-center border border-slate-200 dark:border-slate-700">
        {/* Provider + model segment */}
        <div className="flex items-center gap-1.5 px-2 py-1 bg-slate-50 dark:bg-arti-panel">
          <ProviderIcon
            provider={provider}
            className="h-3 w-3 text-slate-600 dark:text-slate-300"
          />
          <span className="font-mono text-[11px] font-bold text-slate-700 dark:text-slate-200">
            {modelName}
          </span>
        </div>
        {/* Reasoning effort segment */}
        {reasoningEffort && (
          <>
            <span className="h-4 w-px bg-slate-200 dark:bg-slate-700" />
            <div className="flex items-center gap-1 px-2 py-1 bg-white dark:bg-arti-background">
              <EffortIndicator effort={reasoningEffort} />
              <Text variant="label" className={effortColor(reasoningEffort)}>
                {reasoningEffort}
              </Text>
            </div>
          </>
        )}
      </div>
    </div>
  );
};
